import { Component } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { SupplierApiTesterService } from '../../../supplier-api-tester.service';
import { SupplierApiTesterClient } from '../../../supplier-api-tester.client';
import {
  formatSupplierApiAddress,
  formatSupplierApiContact,
  LINE_TYPE_LABELS,
  ORDER_STATUS_LABELS,
  SupplierApiBillable,
  SupplierApiBillableLine,
  SupplierApiCall,
  SupplierApiListQuery,
  SupplierApiOrder,
  SupplierApiOrderLine,
} from '../../../supplier-api-tester.models';
import { ALL_LINES_QUERY, ApiTesterListBase } from '../api-tester-list-base';
import { ApiTesterActionService } from '../api-tester-action-dialog/api-tester-action.service';

/** Parcours guidé des commandes : liste, fiche avec lignes et facturables, décision du fournisseur. */
@Component({
  selector: 'vex-api-tester-orders',
  templateUrl: './api-tester-orders.component.html',
  styleUrls: ['../api-tester-shared.scss'],
})
export class ApiTesterOrdersComponent extends ApiTesterListBase<SupplierApiOrder> {
  readonly statusFilters = [
    { value: '', label: 'Toutes' },
    { value: 'new', label: 'Nouvelles' },
    { value: 'confirmed', label: 'Confirmées' },
    { value: 'rejected', label: 'Refusées' },
  ];
  readonly ORDER_STATUS_LABELS = ORDER_STATUS_LABELS;
  readonly LINE_TYPE_LABELS = LINE_TYPE_LABELS;
  readonly formatAddress = formatSupplierApiAddress;
  readonly formatContact = formatSupplierApiContact;

  order: SupplierApiOrder | null = null;
  orderLines: SupplierApiOrderLine[] = [];
  billables: SupplierApiBillable[] = [];
  detailLoading = false;
  detailError: string | null = null;

  billable: SupplierApiBillable | null = null;
  billableLines: SupplierApiBillableLine[] = [];
  billableLoading = false;
  billableError: string | null = null;

  constructor(
    supplierApiTesterService: SupplierApiTesterService,
    private readonly client: SupplierApiTesterClient,
    private readonly actionService: ApiTesterActionService,
  ) {
    super(supplierApiTesterService);
  }

  protected fetchList(query: SupplierApiListQuery): Observable<SupplierApiCall<SupplierApiOrder[]>> {
    return this.client.listOrders(query);
  }

  protected closeDetail(): void {
    this.order = null;
    this.orderLines = [];
    this.billables = [];
    this.detailError = null;
    this.backToOrder();
  }

  openOrder(order: SupplierApiOrder): void {
    this.order = order;
    this.orderLines = [];
    this.billables = [];
    this.loadOrder();
  }

  loadOrder(): void {
    const orderId = this.order._id;
    this.detailLoading = true;
    forkJoin([
      this.client.getOrder(orderId),
      this.client.getOrderLines(orderId, ALL_LINES_QUERY),
      this.client.listBillables(orderId, ALL_LINES_QUERY),
    ]).subscribe(([orderCall, linesCall, billablesCall]) => {
      this.detailLoading = false;
      this.order = orderCall.data ?? this.order;
      this.orderLines = linesCall.data ?? [];
      this.billables = billablesCall.data ?? [];
      this.detailError = orderCall.errorMessage ?? linesCall.errorMessage ?? billablesCall.errorMessage;
    });
  }

  openBillable(billable: SupplierApiBillable): void {
    this.billable = billable;
    this.billableLines = [];
    const orderId = this.order._id;
    this.billableLoading = true;
    forkJoin([
      this.client.getBillable(orderId, billable._id),
      this.client.getBillableLines(orderId, billable._id, ALL_LINES_QUERY),
    ]).subscribe(([billableCall, linesCall]) => {
      this.billableLoading = false;
      this.billable = billableCall.data ?? this.billable;
      this.billableLines = linesCall.data ?? [];
      this.billableError = billableCall.errorMessage ?? linesCall.errorMessage;
    });
  }

  backToOrder(): void {
    this.billable = null;
    this.billableLines = [];
    this.billableError = null;
  }

  canDecideOrder(): boolean {
    return this.order?.apiStatus === 'new';
  }

  decideOrder(reject: boolean): void {
    const orderId = this.order._id;
    this.actionService
      .run(
        {
          title: reject ? 'Refuser la commande' : 'Confirmer la commande',
          explanation: reject
            ? 'Vous signalez à Predik que vous refusez cette commande. Seul son statut API change, ses lignes ne bougent pas.'
            : 'Vous signalez à Predik que vous acceptez cette commande. Seul son statut API change, ses lignes ne bougent pas.',
          routeId: 'orders-confirm',
          pathParams: { orderId },
          body: { reject },
          danger: reject,
        },
        body => this.client.confirmOrder(orderId, body),
      )
      .subscribe(() => this.loadOrder());
  }
}
